import * as React from "react";
import {
  Columns,
  Stack,
  Column,
  ColumnProps,
  Hidden,
  Heading,
} from "@origin-digital/ods-core";
import { truncate } from "../../helpers/string.helper";
import { TrendingItem, ITrendingItem } from "./TrendingItem";

export enum NumberOfItems {
  None = 0,
  Triple = 3,
  Sextuple = 6,
}

enum Width {
  Half = "1/2",
  OneThird = "1/3",
}

const MAX_HEADING_LENGTH_FOR_THREE_COLUMN = 68;
const MAX_HEADING_LENGTH_FOR_TWO_COLUMN = 130;

interface ITrending {
  title: string;
  items: ITrendingItem[];
}

const rowHasMultilineHeading = (
  row: ITrendingItem[],
  width: Width
): boolean => {
  return (
    row.find(({ heading }) => {
      if (width === Width.Half) {
        return heading.length > MAX_HEADING_LENGTH_FOR_TWO_COLUMN;
      }
      return heading.length > MAX_HEADING_LENGTH_FOR_THREE_COLUMN;
    }) !== undefined
  );
};

export const Trending = ({ title, items }: ITrending) => {
  const numOfItems = items.length;
  const firstRow = items.slice(0, NumberOfItems.Triple);
  const columnWidth =
    firstRow.length < NumberOfItems.Triple ? Width.Half : Width.OneThird;
  const firstRowHasMultilineHeading = rowHasMultilineHeading(
    firstRow,
    columnWidth
  );
  const secondRow = items.slice(NumberOfItems.Triple, NumberOfItems.Sextuple);
  const renderSecondRow = secondRow && secondRow.length > NumberOfItems.None;
  const secondRowHasMultilineHeading = rowHasMultilineHeading(
    secondRow,
    Width.OneThird
  );

  const generateTrendingItem = (
    item: ITrendingItem,
    width: ColumnProps["width"],
    isMultilineRow: boolean
  ) => {
    //truncate heading if multiline
    const maxLength =
      width === Width.Half
        ? MAX_HEADING_LENGTH_FOR_TWO_COLUMN
        : MAX_HEADING_LENGTH_FOR_THREE_COLUMN;
    const truncatedHeading = truncate(item.heading, maxLength);
    return (
      <Column key={item.articleId} width={width}>
        <TrendingItem
          {...item}
          heading={truncatedHeading}
          isMultilineRow={isMultilineRow}
        />
      </Column>
    );
  };

  if (numOfItems === NumberOfItems.None) return null;

  return (
    <Stack data-id="trending" space={["large", "xlarge"]}>
      <Heading variant="h3">{title}</Heading>
      <Columns collapseBelow="lg" space="xlarge">
        {firstRow.map((item) =>
          generateTrendingItem(item, columnWidth, firstRowHasMultilineHeading)
        )}
      </Columns>
      {renderSecondRow ? (
        <Hidden below="lg">
          <Columns collapseBelow="lg" space="xlarge">
            {secondRow.map((item) =>
              generateTrendingItem(
                item,
                Width.OneThird,
                secondRowHasMultilineHeading
              )
            )}
          </Columns>
        </Hidden>
      ) : null}
    </Stack>
  );
};
